import type { Router } from 'vue-router'
import { BASE_URL } from '@/api/baseUrl'

interface LearningTrackingPayload {
  module: string
  action: string
  message: string
  details?: Record<string, any>
  duration_ms?: number
  project_id?: number
  task_id?: number
}

interface ApiTrackingPayload {
  method?: string
  url?: string
  statusCode?: number
  durationMs?: number
  success: boolean
}

const TRACKING_ENDPOINT = `${BASE_URL}/learning/events`
const THROTTLE_WINDOW_MS = 1200

let initialized = false
const recentEvents = new Map<string, number>()

function getAuthToken(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  return localStorage.getItem('token')
}

function shouldSkipTracking(payload: LearningTrackingPayload): boolean {
  const token = getAuthToken()
  if (!token) {
    return true
  }

  const detailsKey = JSON.stringify(payload.details || {})
  const dedupeKey = `${payload.module}|${payload.action}|${payload.message}|${detailsKey}`
  const now = Date.now()
  const lastSeen = recentEvents.get(dedupeKey)
  if (lastSeen && now - lastSeen < THROTTLE_WINDOW_MS) {
    return true
  }

  recentEvents.set(dedupeKey, now)

  if (recentEvents.size > 80) {
    for (const [key, value] of recentEvents.entries()) {
      if (now - value > THROTTLE_WINDOW_MS) {
        recentEvents.delete(key)
      }
    }
  }

  return false
}

export function trackLearningEvent(payload: LearningTrackingPayload) {
  if (typeof window === 'undefined') {
    return
  }

  if (shouldSkipTracking(payload)) {
    return
  }

  const token = getAuthToken()
  if (!token) {
    return
  }

  void fetch(TRACKING_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => undefined)
}

function extractElementText(element: HTMLElement): string {
  const candidates = [
    element.getAttribute('aria-label'),
    element.getAttribute('title'),
    element.textContent,
  ]

  for (const candidate of candidates) {
    if (candidate && candidate.trim()) {
      return candidate.trim().replace(/\s+/g, ' ').slice(0, 80)
    }
  }

  return element.tagName.toLowerCase()
}

function handleDocumentClick(event: Event) {
  const target = event.target as HTMLElement | null
  const clickable = target?.closest('button, a, [role="button"], .el-button') as HTMLElement | null
  if (!clickable) {
    return
  }

  trackLearningEvent({
    module: 'frontend.click',
    action: 'click',
    message: `点击 ${extractElementText(clickable)}`,
    details: {
      path: window.location.pathname,
      element_text: extractElementText(clickable),
      element_type: clickable.tagName.toLowerCase(),
    },
  })
}

function handleFormSubmit(event: Event) {
  const target = event.target as HTMLFormElement | null
  if (!target) {
    return
  }

  trackLearningEvent({
    module: 'frontend.click',
    action: 'submit',
    message: `提交表单 ${window.location.pathname}`,
    details: {
      path: window.location.pathname,
      form_action: target.getAttribute('action') || '',
      form_name: target.getAttribute('name') || '',
    },
  })
}

function extractFieldLabel(element: HTMLElement): string {
  const formItem = element.closest('.el-form-item') as HTMLElement | null
  const label = formItem?.querySelector('.el-form-item__label')?.textContent?.trim()
  if (label) {
    return label.replace(/[:：]\s*$/, '')
  }

  const placeholder = (element.getAttribute('placeholder') || '').trim()
  if (placeholder) {
    return placeholder.slice(0, 40)
  }

  return element.getAttribute('name') || element.getAttribute('aria-label') || element.tagName.toLowerCase()
}

function handleFieldChange(event: Event) {
  const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null
  if (!target) {
    return
  }

  const tagName = target.tagName.toLowerCase()
  if (!['input', 'textarea', 'select'].includes(tagName)) {
    return
  }

  const type = (target.getAttribute('type') || '').toLowerCase()
  if (['password', 'hidden', 'file'].includes(type)) {
    return
  }

  const value = typeof target.value === 'string' ? target.value.trim() : ''
  trackLearningEvent({
    module: 'frontend.click',
    action: 'change',
    message: `更新字段 ${extractFieldLabel(target)}`,
    details: {
      path: window.location.pathname,
      field_label: extractFieldLabel(target),
      field_type: tagName,
      has_value: Boolean(value),
      value_length: value.length,
    },
  })
}

export function trackApiOperation(payload: ApiTrackingPayload) {
  const normalizedUrl = (payload.url || '').split('?')[0]
  if (!normalizedUrl || normalizedUrl.startsWith('/learning')) {
    return
  }

  trackLearningEvent({
    module: 'frontend.api',
    action: payload.success ? 'success' : 'failure',
    message: `${(payload.method || 'GET').toUpperCase()} ${normalizedUrl}`,
    duration_ms: payload.durationMs,
    details: {
      endpoint: normalizedUrl,
      method: (payload.method || 'GET').toUpperCase(),
      status_code: payload.statusCode || null,
      status: payload.success ? 'success' : 'failed',
    },
  })
}

export function initializeLearningTracking(router: Router) {
  if (initialized || typeof window === 'undefined') {
    return
  }

  initialized = true

  router.afterEach((to, from) => {
    trackLearningEvent({
      module: 'frontend.route',
      action: from.fullPath === to.fullPath ? 'view' : 'navigate',
      message: `访问 ${to.meta.title || to.name || to.path}`,
      details: {
        path: to.fullPath,
        from: from.fullPath,
        title: to.meta.title || '',
      },
    })
  })

  document.addEventListener('click', handleDocumentClick, true)
  document.addEventListener('submit', handleFormSubmit, true)
  document.addEventListener('change', handleFieldChange, true)
}